import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Loading, Accordion, AccordionItem } from 'carbon-components-react';
import { useSelector } from 'react-redux';
import { selectedReferral, selectReferralLoader } from 'redux/referrals';
import { selectLodgeEnquiry } from 'redux/enquiries';
import {
  getJobStatusCodeMsg,
  getLocationTypesCodeMsg,
  getActivityTypesCodeMsg
} from 'appConstants/enquiry';
import Utils from 'utilities/helper.util';
import { NoImage32 } from '@carbon/icons-react';
import { phoneNumberFormatter } from 'utilities/validation';

export const ReferralDetailsPanelBody: React.FC<Props> = ({ isListView }) => {
  const referral: any = useSelector(selectedReferral);
  const lodgeEnquiry: any = useSelector(selectLodgeEnquiry);
  const isDataLoading: boolean = useSelector(selectReferralLoader);
  const [enquiry, setEnquiry] = useState<any>();
  const [miniMapUrl, setMiniMapUrl] = useState<string>('');

  useEffect(() => {
    if (referral && lodgeEnquiry) {
      const { designAct, excavationAct } = lodgeEnquiry;
      const { Enquiry } = referral;
      const {
        MapImageFile,
        locationTypes,
        locationsInRoad,
        activityTypes,
        isPlanningJob
      } = Enquiry;
      const newLocationTypes: string[] = (
        locationTypes ?? []
      ).map((code: string) => getLocationTypesCodeMsg(code));
      let newActivityTypes: string[] = [];
      (activityTypes ?? []).forEach((activity: string) => {
        if (activity.search(',') !== -1) {
          const activityArr = activity
            .split(',')
            .map((item: string) =>
              getActivityTypesCodeMsg(
                item.toUpperCase(),
                designAct,
                excavationAct,
                isPlanningJob
              )
            );
          newActivityTypes = [...newActivityTypes, ...activityArr];
        } else {
          newActivityTypes = [
            ...newActivityTypes,
            getActivityTypesCodeMsg(
              activity.toUpperCase(),
              designAct,
              excavationAct,
              isPlanningJob
            )
          ];
        }
      });
      const enquiry = {
        ...Enquiry,
        locationTypes: newLocationTypes.join(', '),
        locationsInRoad: locationsInRoad.join(', '),
        activityTypes: newActivityTypes
      };
      setEnquiry(enquiry);
      MapImageFile?.url && setMiniMapUrl(MapImageFile.url);
    }
  }, [referral, lodgeEnquiry]);

  const formatPhoneNumber = (phoneNumber: string) => {
    return phoneNumberFormatter({
      phoneNumber: phoneNumber
    }).getNationalNumberFormat();
  };

  return (
    <div
      id="referralRightPanelBody"
      data-testid="referralRightPanelBody"
      className="panel--body"
    >
      {isDataLoading || !enquiry ? (
        <div className="loader--wrapper">
          <Loading description="Active loading indicator" withOverlay={false} />
        </div>
      ) : (
        <>
          <div className="panel--body-job-id">Job {enquiry.externalId}</div>
          <hr className="section--divider" />
          <h2
            className={`panel--body-sub-heading${!isListView ? ' no-map' : ''}`}
          >
            {getJobStatusCodeMsg(referral.status)}
          </h2>
          {isListView && (
            <div className="panel--body-map">
              {miniMapUrl ? <img src={miniMapUrl} alt="" /> : <NoImage32 />}
            </div>
          )}
          <Accordion>
            <AccordionItem title="REFERRAL" open={true}>
              <div className="content">
                <div>Enquiry date</div>
                <div>{Utils.toLocalDateTimeFormat(enquiry.createdAt)}</div>
              </div>
              <div className="content">
                <div>Job dates</div>
                <div>
                  {Utils.toLocalDateFormat(enquiry.digStartAt)} ⟶{' '}
                  {Utils.toLocalDateFormat(enquiry.digEndAt)}
                </div>
              </div>
              <div className="content">
                <div>Activity</div>
                {enquiry.activityTypes.map(
                  (activity: string, index: number) => {
                    return <div key={index}>{activity}</div>;
                  }
                )}
              </div>
            </AccordionItem>
            <AccordionItem title="LOCATION" open={true}>
              <div className="content">
                <div>Job site</div>
                <div>{Utils.getFullAddress(enquiry.Address)}</div>
              </div>
              <div className="content">
                <div>Workplace location</div>
                <div>{enquiry.locationTypes}</div>
              </div>
              {enquiry.locationsInRoad && (
                <div className="content">
                  <div>Road location</div>
                  <div>{enquiry.locationsInRoad}</div>
                </div>
              )}
            </AccordionItem>
            <AccordionItem title="ENQUIRER" open={true}>
              <div className="content">
                <div>
                  {enquiry.User.firstName} {enquiry.User.lastName}
                </div>
                {enquiry.User.companyName && (
                  <div>{enquiry.User.companyName}</div>
                )}
                {enquiry.User.Address && (
                  <div>{Utils.getFullAddress(enquiry.User.Address)}</div>
                )}
                <div>{enquiry.User.email}</div>
                {enquiry.User.phoneNumber && (
                  <div>{formatPhoneNumber(enquiry.User.phoneNumber)}</div>
                )}
              </div>
              <div className="content">
                <div>On behalf of</div>
                <div>
                  {enquiry?.WorkingForAuthority?.name ||
                    enquiry?.otherAuthorityName ||
                    'Private'}
                </div>
              </div>
            </AccordionItem>
            {(enquiry.userReference || enquiry.notes) && (
              <AccordionItem title="COMMENTS" open={true}>
                {enquiry.userReference && (
                  <div className="content">
                    <div>User reference</div>
                    <div>{enquiry.userReference}</div>
                  </div>
                )}
                {enquiry.notes && (
                  <div className="content">
                    <div>Notes</div>
                    <div>{enquiry.notes}</div>
                  </div>
                )}
              </AccordionItem>
            )}
          </Accordion>
        </>
      )}
    </div>
  );
};

const propTypes = {
  referralId: PropTypes.number,
  isListView: PropTypes.bool
};

type Props = PropTypes.InferProps<typeof propTypes>;

ReferralDetailsPanelBody.propTypes = propTypes;

ReferralDetailsPanelBody.defaultProps = {
  referralId: undefined,
  isListView: false
};

export default React.memo(ReferralDetailsPanelBody);
